import React, { useEffect, useMemo, useRef, useState } from "react";
import { useClickOutside } from "../../hooks/ClickOutside";
import { MonthNumber } from "../../constants/MonthNumber";
import { useDispatch, useSelector } from "react-redux";
import { ITimesheet, IUser } from "../../models";
import { setExpenses, setTasks } from "../../storage/toolkit";
import SwiperCore, { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { SetTimesheet } from "../../api/SetTimesheet";
import { Years } from "../../constants/Years";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { getApiLink } from "../../functions/getApiLink";
import setCookie from "../../functions/setCookie";
import getCookies from "../../functions/getCookie";
import { SetSummaryEmployees } from "../../api/SetSummaryEmployees";
import { getBearer } from "../../functions/getBearer";

SwiperCore.use([Navigation]);

interface ITableSelectYearMonthProps {
  setMonth?: (m: number) => void;
  onSwitch?: (m: number) => void;
  setYear?: (y: number) => void;
  disabled?: boolean;
}

export const TableSelectYearMonth: React.FC<ITableSelectYearMonthProps> = ({
  setMonth,
  setYear,
  onSwitch,
  disabled = false,
}) => {
  const [isSelectActive, setIsSelectActive] = useState(false);
  const { rootEl } = useClickOutside(setIsSelectActive);

  const dispatch = useDispatch();
  const timesheet: ITimesheet[] = useSelector((state: any) => state.toolkit.timesheet);
  const user: IUser = useSelector((state: any) => state.toolkit.user);
  const chosenTimesheet: ITimesheet = useSelector((state: any) => state.toolkit.chosenTimesheet);

  const { timesheetId } = useParams<{ timesheetId?: string }>();
  const [searchParams] = useSearchParams();

  const dateNow = new Date();
  const currentMonth = dateNow.getMonth() + 1;
  const currentYear = dateNow.getFullYear();

  const isMainPage = !timesheetId;

  const monthsMap = useMemo(() => MonthNumber(), []);
  const yearsList = useMemo(() => Years(), []);

  // ---------------------------
  // StrictMode guard (React 18 dev): prevent double effect “reload feeling”
  // ---------------------------
  const didInitialFetchRef = useRef(false);

  // ---------------------------
  // Abort + race control
  // ---------------------------
  const abortRef = useRef<AbortController | null>(null);
  const requestSeqRef = useRef(0);

  // ---------------------------
  // Debounce state for month/year (removes "проскакування")
  // ---------------------------
  const debounceRef = useRef<number | null>(null);
  const [debouncedMonth, setDebouncedMonth] = useState<number | null>(null);
  const [debouncedYear, setDebouncedYear] = useState<number | null>(null);

  // ===== Auth helpers =====
  const resolveAuthHeader = (): string | null => {
    const fromAxiosDefault = axios.defaults.headers.common?.["Authorization"];
    if (
      fromAxiosDefault &&
      String(fromAxiosDefault).trim() &&
      !String(fromAxiosDefault).includes("undefined")
    ) {
      return String(fromAxiosDefault);
    }

    const cesToken = getCookies("access_token_ces");
    if (cesToken) return cesToken.startsWith("Bearer ") ? cesToken : `Bearer ${cesToken}`;

    const cookieCandidates = [
      getCookies("access_token"),
      getCookies("accessToken"),
      getCookies("token"),
      getCookies("jwt"),
      getCookies("Authorization"),
    ].filter(Boolean) as string[];

    const cookieToken = cookieCandidates[0];
    if (cookieToken) return cookieToken.startsWith("Bearer ") ? cookieToken : `Bearer ${cookieToken}`;

    return null;
  };

  // ===== Initial month/year =====
  const initial = useMemo(() => {
    // When viewing a specific timesheet, use chosenTimesheet.date
    if (timesheetId && chosenTimesheet?.date) {
      const parts = chosenTimesheet.date.split("/");
      if (parts.length === 3) {
        const month = Number(parts[1]);
        const yearRaw = parts[2];
        const year = yearRaw.length <= 2 ? 2000 + Number(yearRaw) : Number(yearRaw);
        if (month >= 1 && month <= 12 && year >= 2000 && year <= 2100) {
          return { month, year };
        }
      }
      // Fallback to current if parsing fails
      return { month: currentMonth, year: currentYear };
    }
    
    // Main page: use current date (capped to current month)
    if (isMainPage) {
      const cookieMonth = getCookies("timesheetMonth");
      const cookieYear = getCookies("timesheetYear");
      let m = cookieMonth ? Number(cookieMonth) : currentMonth;
      let y = cookieYear ? Number(cookieYear) : currentYear;
      // Cap to current month - never show future
      if (y > currentYear) { y = currentYear; m = currentMonth; }
      if (y === currentYear && m > currentMonth) { m = currentMonth; }
      return { month: m, year: y };
    }

    // Fallback for other cases
    const spMonth = searchParams.get("month");
    const spYear = searchParams.get("year");
    const cookieMonth = getCookies("timesheetMonth");
    const cookieYear = getCookies("timesheetYear");

    let month =
      (spMonth ? Number(spMonth) : null) ||
      (cookieMonth ? Number(cookieMonth) : null) ||
      currentMonth;

    let year =
      (spYear ? Number(spYear) : null) ||
      (cookieYear ? Number(cookieYear) : null) ||
      currentYear;

    // Cap to current month - never show future
    if (year > currentYear) { year = currentYear; month = currentMonth; }
    if (year === currentYear && month > currentMonth) { month = currentMonth; }

    return { month, year };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMainPage, currentMonth, currentYear, timesheetId, chosenTimesheet?.date, searchParams]);

  const [filedMonth, setFieldMonth] = useState<number>(initial.month);
  const [fieldYear, setFieldYear] = useState<number>(initial.year);

  // ---------------------------
  // When viewing a specific timesheet, sync with chosenTimesheet.date
  // ---------------------------
  useEffect(() => {
    if (!timesheetId) return;
    if (!chosenTimesheet?.date) return;
    
    // Parse chosenTimesheet.date format: "DD/MM/YY" or "DD/MM/YYYY"
    const parts = chosenTimesheet.date.split("/");
    if (parts.length !== 3) return;
    
    const month = Number(parts[1]);
    const yearRaw = parts[2];
    const year = yearRaw.length <= 2 ? 2000 + Number(yearRaw) : Number(yearRaw);
    
    if (month >= 1 && month <= 12) setFieldMonth(month);
    if (year >= 2000 && year <= 2100) setFieldYear(year);
  }, [timesheetId, chosenTimesheet?.date]);

  // ---------------------------
  // Initialize month/year for main pages (only on first mount)
  // ---------------------------
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!isMainPage) return;
    if (timesheetId) return;
    if (initializedRef.current) return; // Don't force-reset on re-renders
    initializedRef.current = true;

    // Use cookie values if available (user may have picked a different year/month before)
    const cookieMonth = getCookies("timesheetMonth");
    const cookieYear = getCookies("timesheetYear");

    let initMonth = cookieMonth ? Number(cookieMonth) : currentMonth;
    let initYear = cookieYear ? Number(cookieYear) : currentYear;

    // Never allow future months for current year
    if (initYear === currentYear && initMonth > currentMonth) {
      initMonth = currentMonth;
    }
    // Never allow future years
    if (initYear > currentYear) {
      initYear = currentYear;
      initMonth = currentMonth;
    }

    setFieldMonth(initMonth);
    setFieldYear(initYear);

    // Sync parent state so it matches what the calendar displays
    setMonth?.(initMonth);
    setYear?.(initYear);
  }, [isMainPage, currentMonth, currentYear, timesheetId]);

  const isMonthAvailable = (month: number, year: number) => {
    if (timesheetId) return true;
    if (year < currentYear) return true;
    if (year === currentYear) return month <= currentMonth;
    return false;
  };

  const buildBaseQuery = (month: number, year: number, tsId?: string) => {
    if (tsId) return `timesheet_id=${tsId}`;
    return `month=${month}&year=${year}&user_id=${user?.id || ""}`;
  };

  // ---------------------------
  // Normalize path for getApiLink:
  // ALWAYS pass without leading "/api"
  // ---------------------------
  const normalizeApiPath = (p: string) => p.replace(/^\/+/, "").replace(/^api\/+/, "");

  // Build 2 variants because your env sometimes requires /api/api/...
  const buildApiUrlVariants = (pathWithoutLeadingSlash: string) => {
    const clean = normalizeApiPath(pathWithoutLeadingSlash); // timesheet/tasks/?...
    const urlA = getApiLink(clean); // usually -> /api/...
    const urlB = getApiLink(`api/${clean}`); // fallback -> /api/api/...
    return { urlA, urlB };
  };

  const axiosGetWith404Fallback = async (
    urlA: string,
    urlB: string,
    auth: string,
    signal: AbortSignal
  ) => {
    try {
      return await axios.get(urlA, { headers: { Authorization: auth }, signal });
    } catch (err: any) {
      if (err?.response?.status === 404) {
        return await axios.get(urlB, { headers: { Authorization: auth }, signal });
      }
      throw err;
    }
  };

  // ---------------------------
  // Debounce month/year changes (prevents request spam + UI flicker)
  // ---------------------------
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);

    debounceRef.current = window.setTimeout(() => {
      setDebouncedMonth(filedMonth);
      setDebouncedYear(fieldYear);
    }, 120);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [filedMonth, fieldYear]);

  // ===== Central fetch tasks/expenses (only when debounced values ready) =====
  useEffect(() => {
    if (debouncedMonth == null || debouncedYear == null) return;

    // When viewing a specific timesheet, Timesheet.tsx handles data loading
    // Don't duplicate requests here - it causes flickering
    if (timesheetId) return;

    // For main pages we need user.id
    if (!user?.id) return;

    // React 18 StrictMode dev double-fire: ignore second initial run
    if (!didInitialFetchRef.current) {
      didInitialFetchRef.current = true;
    } else {
      // allow all subsequent real changes
    }

    getBearer("get");
    const auth = resolveAuthHeader();
    if (!auth) {
      console.warn("SKIP: немає токена (Authorization).");
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const seq = ++requestSeqRef.current;
    const base = buildBaseQuery(debouncedMonth, debouncedYear, timesheetId);

    const tasks = buildApiUrlVariants(`timesheet/tasks/?${base}`);
    const expenses = buildApiUrlVariants(`timesheet/expenses/?${base}`);

    (async () => {
      try {
        const [tasksRes, expensesRes] = await Promise.all([
          axiosGetWith404Fallback(tasks.urlA, tasks.urlB, auth, controller.signal),
          axiosGetWith404Fallback(expenses.urlA, expenses.urlB, auth, controller.signal),
        ]);

        if (seq !== requestSeqRef.current) return;

        dispatch(setTasks(Array.isArray(tasksRes.data) ? tasksRes.data : []));
        dispatch(setExpenses(Array.isArray(expensesRes.data) ? expensesRes.data : []));
      } catch (err: any) {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;

        // IMPORTANT:
        // Do NOT wipe lists on 404/400 -> it causes "empty page flicker"
        // If backend truly has no data, it should return 200 with [].
        console.error("LOAD failed:", {
          status: err?.response?.status,
          message: err?.response?.data?.detail || err?.message,
          url: err?.config?.url,
        });
      }
    })();

    return () => controller.abort();
  }, [dispatch, debouncedMonth, debouncedYear, timesheetId, user?.id]);

  // ===== Timesheet list + summary-employees =====
  useEffect(() => {
    // Prevent calling SetTimesheet before auth/user ready if your API depends on it
    // (common cause of "reload/empty")
    if (!user) return;

    // When viewing specific timesheet, don't reload data here - Timesheet.tsx handles it
    if (timesheetId) {
      return;
    }

    // call only when year changes (not every render)
    SetTimesheet(dispatch, fieldYear);
  }, [dispatch, timesheetId, user, fieldYear]);

  // ===== handlers =====
  const handleChooseMonth = (month: number) => {
    if (!isMonthAvailable(month, fieldYear)) return;

    setFieldMonth(month);
    
    // Only update cookies when on main page (not viewing specific timesheet)
    if (!timesheetId) {
      setCookie("timesheetMonth", String(month));
    }

    onSwitch?.(month);
    setMonth?.(month);
  };

  const handleChangeYear = (year: number) => {
    if (!timesheetId && year > currentYear) return;

    setFieldYear(year);
    
    // When switching to current year, if selected month is in the future, switch to current month
    if (year === currentYear && filedMonth > currentMonth) {
      setFieldMonth(currentMonth);
      if (!timesheetId) {
        setCookie("timesheetMonth", String(currentMonth));
      }
      onSwitch?.(currentMonth);
      setMonth?.(currentMonth);
    }
    
    // Only update cookies when on main page (not viewing specific timesheet)
    if (!timesheetId) {
      setCookie("timesheetYear", String(year));
    }
    
    setYear?.(year);
  };

  const initialYearSlide = useMemo(() => {
    const idx = yearsList.findIndex((y) => y.year === fieldYear);
    return idx >= 0 ? idx : 0;
  }, [yearsList, fieldYear]);

  // Disable calendar when viewing a specific timesheet (timesheetId in URL)
  const isDisabled = disabled || !!timesheetId;

  return (
    <div
      ref={rootEl}
      className={
        isSelectActive && !isDisabled
          ? "section-table__change-full-date drop-down is-active"
          : "section-table__change-full-date drop-down"
      }
    >
      <button
        className="section-table__change-full-date--target drop-down__target"
        type="button"
        onClick={() => !isDisabled && setIsSelectActive((prev) => !prev)}
        disabled={isDisabled}
        style={isDisabled ? { cursor: 'default', opacity: 1 } : {}}
      >
        <span>
          {monthsMap[filedMonth]?.translate}, {fieldYear}
        </span>
        {!isDisabled && (
          <svg
            width="10"
            height="7"
            viewBox="0 0 10 7"
            className="section-table__change-full-date--target-arrow drop-down__target--arrow"
          >
            <use xlinkHref="#drop-down-arrow"></use>
          </svg>
        )}
      </button>

      <div className="section-table__change-full-date--block drop-down__block">
        <div className="section-table__change-full-date--months">
          <ul>
            {Object.keys(monthsMap).map((key) => {
              const monthNum = Number(key);
              const isDisabled = timesheetId ? false : !isMonthAvailable(monthNum, fieldYear);

              return (
                <li key={key} className={isDisabled ? "is-disabled" : ""}>
                  <label>
                    <input
                      type="radio"
                      name="month"
                      value={key}
                      checked={monthNum === filedMonth}
                      disabled={isDisabled}
                      onChange={() => !isDisabled && handleChooseMonth(monthNum)}
                    />
                    <span>{monthsMap[monthNum]?.translate}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="section-table__change-full-date--slider splide">
          <div className="splide__track">
            <Swiper
              slidesPerView={3}
              navigation={{
                nextEl: ".splide__arrow--next",
                prevEl: ".splide__arrow--prev",
              }}
              modules={[Navigation]}
              initialSlide={initialYearSlide}
              watchOverflow
            >
              {yearsList.map((yearObj) => {
                const isYearDisabled = timesheetId ? false : yearObj.year > currentYear;

                return (
                  <SwiperSlide key={yearObj.year}>
                    <li className={`splide__slide ${isYearDisabled ? "is-disabled" : ""}`}>
                      <label>
                        <input
                          type="radio"
                          name="year"
                          value={yearObj.year}
                          checked={fieldYear === yearObj.year}
                          disabled={isYearDisabled}
                          onChange={() => !isYearDisabled && handleChangeYear(yearObj.year)}
                        />
                        <span>{yearObj.year}</span>
                      </label>
                    </li>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>

          <div className="splide__arrows">
            <button className="splide__arrow splide__arrow--prev" type="button">
              <svg width="7" height="10" viewBox="0 0 7 10">
                <use xlinkHref="#arrow-prev"></use>
              </svg>
            </button>
            <button className="splide__arrow splide__arrow--next" type="button">
              <svg width="7" height="10" viewBox="0 0 7 10">
                <use xlinkHref="#arrow-next"></use>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};